package com.iregained.site.components.sections.news.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.news.styles.*
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Headline Drop (3D tilt and drop)
@Composable
private fun Modifier.headlineDrop(isVisible: Boolean, delayMs: Int = 0): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else (-40).px)
        .rotateX(if (isVisible) 0.deg else 15.deg)
        .styleModifier { property("transform-origin", "top center"); property("perspective", "1000px") }.transition(
            Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
fun NewsFeaturedSection(data: NewsFeaturedSectionData = defaultNewsFeaturedData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "news-featured-section"
    var isVisible by remember { mutableStateOf(false) }

    if (data.featuredStory.headline.isNotBlank()) {
        Column(
            modifier = NewsFeaturedContainerStyle.toModifier().id(targetId).fillMaxWidth()
                .backgroundColor(Colors.White)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                    .justifyContent(JustifyContent.Center).toAttrs {
                    ref { element ->
                        val observer = IntersectionObserver { entries, obs ->
                            entries.forEach {
                                if (it.isIntersecting) {
                                    isVisible = true; obs.unobserve(element)
                                }
                            }
                        }
                        observer.observe(element); onDispose { observer.disconnect() }
                    }
                }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.pageTitle.isNotBlank()) {
                        SectionHeader(
                            text = data.pageTitle,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 16.px).headlineDrop(isVisible, 0),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    if (data.pageSubtitle.isNotBlank()) {
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().maxWidth(1200.px).fontSize(18.px)
                                .fontWeight(FontWeight.Medium).color(palette.custom.darkText)
                                .textAlign(TextAlign.Center).margin(bottom = 60.px).headlineDrop(isVisible, 150)
                                .toAttrs {
                                    ref { htmlElement -> htmlElement.innerHTML = data.pageSubtitle; onDispose {} }
                                })
                    }

                    if (data.featuredStory.linkUrl.isNotBlank()) {
                        Link(
                            path = data.featuredStory.linkUrl,
                            modifier = Modifier.textDecorationLine(com.varabyte.kobweb.compose.css.TextDecorationLine.None)
                        ) {
                            Box(modifier = FeaturedCardWrapperStyle.toModifier().headlineDrop(isVisible, 300)) {
                                SimpleGrid(
                                    numColumns(1, lg = 2), modifier = FeaturedCardGridStyle.toModifier()
                                ) {
                                    Box(modifier = FeaturedImageWrapperStyle.toModifier()) {
                                        Image(
                                            src = data.featuredStory.imagePath,
                                            alt = data.featuredStory.headline,
                                            modifier = FeaturedImageStyle.toModifier().objectFit(ObjectFit.ScaleDown)
                                        )
                                    }

                                    Column(modifier = FeaturedContentColumnStyle.toModifier()) {
                                        SpanText(
                                            text = data.featuredStory.categoryTag,
                                            modifier = FeaturedTagStyle.toModifier()
                                        )
                                        SpanText(
                                            text = data.featuredStory.headline,
                                            modifier = FeaturedHeadlineStyle.toModifier()
                                        )
                                        Div(
                                            attrs = FeaturedSummaryStyle.toModifier().toAttrs {
                                                ref { htmlElement ->
                                                    htmlElement.innerHTML = data.featuredStory.summary; onDispose {}
                                                }
                                            })
                                        Row(modifier = FeaturedMetaRowStyle.toModifier()) {
                                            SpanText(
                                                text = data.featuredStory.datePublished,
                                                modifier = FeaturedDateStyle.toModifier()
                                            )
                                            if (data.featuredStory.linkUrlDescription.isNotBlank()) {
                                                SpanText(
                                                    text = data.featuredStory.linkUrlDescription,
                                                    modifier = FeaturedLinkStyle.toModifier()
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL
// ==========================================
/**
 * CMS CONTENT & IMAGE UPLOAD SPECS FOR FEATURED NEWS STORY:
 * * @field imagePath (inside FeaturedStoryData):
 * - Aspect Ratio: 1:1 (Square) for Desktop / 16:9 for Mobile.
 * - Recommended Dimensions: 1000x1000px.
 * - Max File Size: 350KB (WebP or JPEG).
 * - Format Constraints: Highly visual, editorial photography (e.g., clinical facilities,
 * active patient recovery, high-fidelity hardware). Avoid flat text graphics or white-padded logos.
 * - Behavior: Renders as a 50/50 split alongside the editorial text on large screens. Tightly cropped
 * to its boundaries to maintain symmetry with the matching content block.
 * * @field summary (inside FeaturedStoryData):
 * - Format: Parsed HTML string (WYSIWYG/Markdown compatible).
 * - Character Limit: Recommended 200–250 characters (approximately 3–4 lines).
 * - Constraint: Keep summaries punchy. Because this is the primary hero focal point of the page,
 * exceeding this limit pushes the "Read Full Story" link below the natural fold of the card.
 */
data class FeaturedStoryData(
    val categoryTag: String,
    val headline: String,
    val summary: String,
    val datePublished: String,
    val imagePath: String,
    val linkUrl: String,
    val linkUrlDescription: String
)

data class NewsFeaturedSectionData(
    val sectionId: String? = null,
    val pageTitle: String = "NEWS & MEDIA HUB",
    val pageSubtitle: String = "The latest updates on clinical deployments, regulatory milestones, and how the MyHand<sup>®</sup> System is transforming neurorehabilitation globally.",
//    val featuredStory: FeaturedStoryData = FeaturedStoryData(
//        categoryTag = "Regulatory & Compliance",
//        headline = "Manipal Hospital deploys Advanced MyHand® System for inpatient Stroke Recovery",
//        summary = "In a landmark partnership, Manipal Hospital has integrated the MyHand® unit into their neurorehabilitation ward, marking a significant leap forward in standardizing high-repetition therapy for acute stroke survivors.",
//        datePublished = "May 11, 2026", // Example date
//        imagePath = Res.Images.News.Feature.FEATURE_COVER,
//        linkUrl = "https://www.sudbury.com/local-business/sudbury-med-tech-device-approved-for-sale-in-india-11010892",
//        linkUrlDescription = "Read Full Story →"
//    )

    val featuredStory: FeaturedStoryData = FeaturedStoryData(
        categoryTag = "Regulatory & Compliance",
        headline = "Milestone for Sudbury Med-Tech: IRegained Inc. Gains Access to Indian Healthcare Market",
        summary = "Sudbury’s IRegained Inc. is expanding into India after securing regulatory approval for its MyHand<sup>®</sup> rehabilitation system. This milestone grants millions of stroke survivors in the Indo-Pacific access to the company’s neuroplasticity-driven technology, significantly scaling the Northern Ontario firm’s global footprint within a major healthcare market.",
        datePublished = "July 30, 2025", // Example date
        imagePath = Res.Images.News.Grid.SUDBURY_COM,
        linkUrl = "https://www.sudbury.com/local-business/sudbury-med-tech-device-approved-for-sale-in-india-11010892",
        linkUrlDescription = "Read Full Story →"
    )
)

val defaultNewsFeaturedData: NewsFeaturedSectionData = NewsFeaturedSectionData()